import { toast } from '@Backlot-Cars/archie';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getMarketplaceSourceTabFromTabName, MARKETPLACE_TAB } from 'constants/marketplace';
import STATUS_CODES from 'constants/statusCodes';
import { CONTEXT } from 'helpers/search';
import { treatments } from 'helpers/split/treatments';
import BidSalesService from 'services/timedSales/buyside/bidsales.service';
import BuysideSearchService from 'services/timedSales/buyside/search.service';
import { updateSyncedTabsAfterFetch } from 'store/slices/auctionPoolingUpdater/auctionPoolingSlice';
import { addVehicleToList, updateBids } from 'store/slices/buyerVehicles/buyerVehicles';
import { getRefreshDataForVehicleIds } from 'store/slices/buyerVehicles/thunks/helpers/getOfferRefreshData';
import { isVehicleOnPills, offerSummaryWasLoadedSelector } from 'store/slices/offerSummary/offerSummarySelector';
import { getIsTreatmentEnabled } from 'store/slices/treatments/selector';
import { getFiltersParams } from '../buyers/filters/filtersSelectors';
import { updateAppliedFilters } from '../buyers/filters/filtersSlice';
import { getListConfig } from './buyerSelectors';
import * as localFunctions from './buyerVehiclesThunks';
import parseConfigurableEndpoint from './helpers/parseConfigurableEndpoint';
import { loadFeaturedVehicleCampaign, shouldLoadFeaturedVehicleCampaign } from './thunks/featuredVehicles';
import { removeVehicleFromWatchlist } from './thunks/watchlist';

const NUMBER_OF_HIDE_ACTIONS_BEFORE_RELOAD_AUCTION_LIST = 10;

const getPageToReloadAfterSuccessfullHide = (
	itemsPerPage,
	itemsOnCurrentPage,
	totalElements,
	currentPage,
	pageCount,
) => {
	const isOnLastPage = currentPage === pageCount;
	const itemsOnCurrentPageAfterHide = itemsOnCurrentPage - 1;
	const thereAreMoreElementsOnOtherPages = totalElements - 1 > itemsOnCurrentPageAfterHide;
	const isPageEmpty = itemsOnCurrentPageAfterHide === 0;
	const hiddenActionsNumberExceeded =
		itemsOnCurrentPageAfterHide <= itemsPerPage - NUMBER_OF_HIDE_ACTIONS_BEFORE_RELOAD_AUCTION_LIST;

	if (isPageEmpty && thereAreMoreElementsOnOtherPages) {
		return isOnLastPage ? currentPage - 1 : currentPage;
	}
	if (hiddenActionsNumberExceeded && thereAreMoreElementsOnOtherPages && !isOnLastPage) return currentPage;

	return false;
};

export const setVehicleAsHidden = createAsyncThunk(
	'setVehicleAsHidden',
	async ({ vehicle, vdpType, unifiedMarketplace, searchListKey }, thunkAPI) => {
		const sourceTab = getMarketplaceSourceTabFromTabName(searchListKey);
		const {
			data: { success },
		} = await BidSalesService.instance.hideVehicle(vehicle.id, vdpType, sourceTab);
		const { reloadSearchOnHide } = getListConfig(thunkAPI.getState().buyerVehicles, searchListKey);

		const vehicleListState = thunkAPI.getState().buyerVehicles;

		const watchlistIds = vehicleListState.searchLists[MARKETPLACE_TAB.watchlist].vehicleIds;
		if (searchListKey !== MARKETPLACE_TAB.watchlist && watchlistIds.includes(vehicle.id)) {
			thunkAPI.dispatch(removeVehicleFromWatchlist({ vehicle, unifiedMarketplace }));
		}

		if (!searchListKey || !reloadSearchOnHide) return success; // this could happens when using it from FeaturedVehicleCampaign or Vdp that doesn't have searchListKey

		// Reload search list if needed
		const {
			pageCount,
			vehicleIds,
			totalElements,
			itemsPerPage,
			currentSearch: {
				params: { page: currentPage },
			},
		} = vehicleListState.searchLists[MARKETPLACE_TAB.marketplace];
		const pageToReload = getPageToReloadAfterSuccessfullHide(
			itemsPerPage,
			vehicleIds.length,
			totalElements,
			currentPage,
			pageCount,
		);

		if (pageToReload) {
			thunkAPI.dispatch(
				localFunctions.loadVehiclesMarketplace({
					page: pageToReload,
					searchListKey: MARKETPLACE_TAB.marketplace,
					unifiedMarketplace,
				}),
			);
		}

		return success;
	},
);

export const unHideVehicle = createAsyncThunk(
	'unHideVehicle',
	async ({ vehicleToUnHide, vdpType, searchListKey, unifiedMarketplace }, thunkAPI) => {
		const sourceTab = getMarketplaceSourceTabFromTabName(searchListKey);
		const {
			data: { success },
		} = await BidSalesService.instance.unHideVehicle(vehicleToUnHide.id, vdpType, sourceTab);

		const vehicleListState = thunkAPI.getState().buyerVehicles;

		// Reload main source list on unhide (marketplace or auction)
		const pageToReload = vehicleListState.searchLists[MARKETPLACE_TAB.marketplace].currentSearch.params.page;

		thunkAPI.dispatch(
			localFunctions.loadVehiclesMarketplace({
				page: pageToReload,
				searchListKey: MARKETPLACE_TAB.marketplace,
				unifiedMarketplace,
			}),
		);

		// Reload hidden list if needed
		const { pageCount, vehicleIds } = vehicleListState.searchLists[MARKETPLACE_TAB.hidden];
		const currentPage = vehicleListState.searchLists[MARKETPLACE_TAB.hidden].currentSearch.params.page;
		const thereAreFollowingPages = currentPage < pageCount;
		const deletingLastElementFromPage = currentPage !== 1 && vehicleIds.length === 1;

		if (thereAreFollowingPages || deletingLastElementFromPage) {
			const page = deletingLastElementFromPage ? currentPage - 1 : currentPage;

			thunkAPI.dispatch(
				localFunctions.loadVehiclesMarketplace({
					page,
					searchListKey: MARKETPLACE_TAB.hidden,
					unifiedMarketplace,
				}),
			);
		}
		return success;
	},
);

// ----------------
// Marketplace
// ----------------
export const hydrateVehiclesOfferRefreshV4 = createAsyncThunk(
	'hydrateVehiclesOfferRefreshV4',
	async (
		{ vehicles, searchListKey, unifiedMarketplace, cancelToken, offerSummarySaleType, isVdp, loggedUser },
		thunkAPI,
	) => {
		const isBuyerNewOfferPayloadStructEnabled = getIsTreatmentEnabled(
			thunkAPI.getState(),
			treatments.backend_driven_offer_payload_react,
		);
		const cancelSource = axios.CancelToken.source();
		const cancelRequest = () => cancelSource.cancel();
		if (thunkAPI.signal.aborted) cancelRequest();
		else thunkAPI.signal.addEventListener('abort', cancelRequest);
		cancelToken?.subscribe?.(cancelRequest);

		try {
			const vehiclesWithOfferData = await getRefreshDataForVehicleIds({
				vehicles,
				searchListKey,
				includeSummary: unifiedMarketplace,
				cancelToken: cancelSource.token,
				offerSummaryWasLoaded: offerSummaryWasLoadedSelector(thunkAPI.getState()),
				dispatch: thunkAPI.dispatch,
				offerSummarySaleType,
				isBuyerNewOfferPayloadStructEnabled,
				isVdp,
			});

			const offerStateRequestId =
				thunkAPI.getState().buyerVehicles.searchLists[searchListKey]?.offerStateRequestId;
			if (thunkAPI.signal.aborted || cancelSource.token.reason || offerStateRequestId !== thunkAPI.requestId) {
				return;
			}

			const offerUpdates =
				isBuyerNewOfferPayloadStructEnabled ?
					vehiclesWithOfferData
						.filter((vehicle) => vehicle.offerData)
						.map(({ id, offerData, version }) => ({ id, offerData, version }))
				:	vehiclesWithOfferData;
			thunkAPI.dispatch(
				updateBids({ vehicles: offerUpdates, loggedUser: loggedUser || thunkAPI.getState()?.loggedUser?.user }),
			);
			return { vehicles: vehiclesWithOfferData };
		} catch (error) {
			if (cancelSource.token.reason) return thunkAPI.rejectWithValue({ cancelled: true });
			throw error;
		} finally {
			thunkAPI.signal.removeEventListener('abort', cancelRequest);
			cancelToken?.unsubscribe?.(cancelRequest);
		}
	},
);

export const loadVehiclesMarketplace = createAsyncThunk(
	'loadVehiclesMarketplace',
	async (
		{ page, errorMessage, searchListKey, skipVehicleCampaign, unifiedMarketplace, endpoint, translate },
		thunkAPI,
	) => {
		const { rejectWithValue, fulfillWithValue, signal, getState } = thunkAPI;
		const state = getState();
		const isBuyerNewOfferPayloadStructEnabled = getIsTreatmentEnabled(
			state,
			treatments.backend_driven_offer_payload_react,
		);

		const { configurableEndpoint, configurableParams } = parseConfigurableEndpoint(endpoint) || {};

		const { params: searchParams } = getFiltersParams(state, searchListKey);

		const currentPage = page || searchParams.page || 1;

		if (
			!skipVehicleCampaign &&
			shouldLoadFeaturedVehicleCampaign({ page: currentPage, searchListKey }) &&
			!signal.aborted
		) {
			thunkAPI.dispatch(loadFeaturedVehicleCampaign({ searchListKey, context: CONTEXT.marketplace }));
		}

		const source = axios.CancelToken.source();
		signal.addEventListener('abort', () => {
			source.cancel();
		});

		const filters = {
			...searchParams,
			...configurableParams,
			source_tab: getMarketplaceSourceTabFromTabName(searchListKey) || searchListKey,
			page: currentPage,
			// Remove when deleting tbd_187929_unified_marketplace_react mixed experience
			sale_types: state.buyerVehicles.saleType || searchParams.sale_types,
			backend_driven_ui: isBuyerNewOfferPayloadStructEnabled,
		};

		try {
			let searchResult;
			searchResult = await BuysideSearchService.instance.searchVehicles({
				params: filters,
				cancelToken: source.token,
				endpoint: configurableEndpoint,
			});

			if (searchResult?.status !== STATUS_CODES.OK) return rejectWithValue({ errorMessage });

			const closedMarketplace = configurableParams?.marketplace_ids;
			if (closedMarketplace && searchResult?.data?.messages?.length > 0) {
				return rejectWithValue({ errorMessage: searchResult?.data?.messages[0] });
			}

			const {
				applied_filters: appliedFilters,
				metadata,
				data: { checkout_warning, vehicles },
			} = searchResult.data;

			if (checkout_warning?.show) toast.warning(checkout_warning.message);
			if (appliedFilters) thunkAPI.dispatch(updateAppliedFilters({ appliedFilters, listKey: searchListKey }));

			const isBuyerNewOfferPayloadStructEnabled = getIsTreatmentEnabled(
				thunkAPI.getState(),
				treatments.backend_driven_offer_payload_react,
			);

			let refreshedVehicles = vehicles;
			if (isBuyerNewOfferPayloadStructEnabled) {
				thunkAPI.dispatch(
					hydrateVehiclesOfferRefreshV4({
						vehicles,
						searchListKey,
						unifiedMarketplace,
						cancelToken: source.token,
						offerSummarySaleType: state.buyerVehicles.saleType,
					}),
				);
			} else {
				refreshedVehicles = await getRefreshDataForVehicleIds({
					vehicles,
					searchListKey,
					includeSummary: unifiedMarketplace,
					cancelToken: source.token,
					offerSummaryWasLoaded: offerSummaryWasLoadedSelector(thunkAPI.getState()),
					dispatch: thunkAPI.dispatch,
					offerSummarySaleType: state.buyerVehicles.saleType,
				});
			}

			thunkAPI.dispatch(updateSyncedTabsAfterFetch({ syncedTab: searchListKey }));
			return fulfillWithValue({
				pageCount: metadata.page_count,
				totalCount: metadata.total_count,
				page: Number(metadata.page),
				vehicles: refreshedVehicles,
			});
		} catch (res) {
			const showPurchasesError = res?.error?.data?.show_pending_purchases_message === true;
			return rejectWithValue({
				errorMessage:
					showPurchasesError && translate ? translate('search:pendingPurchasesError') : errorMessage,
			});
		}
	},
);

export const addToOfferList =
	({ vehicle }) =>
	(dispatch, getState) => {
		const state = getState();
		const isOnPills = isVehicleOnPills(state, vehicle.bid_sale_id);

		if (!isOnPills) {
			dispatch(
				addVehicleToList({
					vehicle,
					searchListKey: MARKETPLACE_TAB.offers,
				}),
			);
		}
	};
